'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    StoreConfig,
    checkStoreLimit,
    deleteStoreConfig,
    getStoreConfigByCompany,
} from '@/lib/api/commerce/store-config'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdDelete,
    MdEdit,
    MdLocalShipping,
    MdPalette,
    MdStorefront,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function StoresListPage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const [loading, setLoading] = useState(true)
    const [stores, setStores] = useState<StoreConfig[]>([])
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [deletingId, setDeletingId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [storeLimitModal, setStoreLimitModal] = useState(false)
    const [storeLimitInfo, setStoreLimitInfo] = useState<{
        newPrice: number
        currentPrice: number
    } | null>(null)

    useEffect(() => {
        loadStores()
    }, [])

    const loadStores = async () => {
        const companyId = user?.company?.id
        if (!companyId) return

        setLoading(true)
        try {
            const res = await getStoreConfigByCompany(companyId)
            if (res.success && res.result) {
                setStores(Array.isArray(res.result) ? res.result : [res.result])
            }
        } catch {
            toast.error('Erro ao carregar lojas')
        } finally {
            setLoading(false)
        }
    }

    const handleCreateStore = async () => {
        const companyId = user?.company?.id
        if (!companyId) {
            navigate('/commerce/stores/new')
            return
        }

        try {
            const res = await checkStoreLimit(companyId)
            if (res.success && res.result) {
                const info = res.result
                if (
                    !info.unlimited &&
                    info.exceedsLimit &&
                    info.extraCost > 0
                ) {
                    setStoreLimitInfo({
                        newPrice: info.newPrice,
                        currentPrice: info.currentPrice || 0,
                    })
                    setStoreLimitModal(true)
                    return
                }
            }
        } catch {
            // Se falhar a verificação, permite criar
        }
        navigate('/commerce/stores/new')
    }

    const handleDelete = async () => {
        if (!deletingId) return

        setDeleting(true)
        try {
            const res = await deleteStoreConfig(deletingId)
            if (res.success) {
                setStores(prev => prev.filter(s => s.id !== deletingId))
                toast.success('Loja removida com sucesso')
            } else {
                toast.error('Erro ao remover loja')
            }
        } catch {
            toast.error('Erro ao remover loja')
        } finally {
            setDeleting(false)
            setDeleteModalOpen(false)
            setDeletingId(null)
        }
    }

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-5xl space-y-6 p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <MdStorefront className="text-secondary text-3xl" />
                    <div>
                        <h1 className="text-2xl font-bold">Minhas Lojas </h1>
                        <p className="text-base-content/60 text-sm">
                            Gerencie suas lojas online
                        </p>
                    </div>
                </div>
                <button className="btn btn-secondary" onClick={handleCreateStore}>
                    <MdAdd className="text-lg" />
                    Nova Loja
                </button>
            </div>

            {stores.length === 0 ? (
                <div className="card bg-base-100 shadow-sm">
                    <div className="card-body items-center text-center">
                        <MdStorefront className="text-base-content/30 text-6xl" />
                        <h3 className="text-lg font-bold">
                            Nenhuma loja criada
                        </h3>
                        <p className="text-base-content/60">
                            Crie sua primeira loja online para começar a vender.
                        </p>
                        <button
                            className="btn btn-secondary btn-sm mt-2"
                            onClick={handleCreateStore}
                        >
                            <MdAdd className="text-lg" />
                            Criar Primeira Loja
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {stores.map(store => (
                        <div
                            key={store.id}
                            className="card bg-base-100 shadow-sm"
                        >
                            <div className="card-body">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        {store.logo ? (
                                            <img
                                                src={store.logo}
                                                alt={store.name}
                                                className="h-12 w-12 rounded-lg border object-cover"
                                            />
                                        ) : (
                                            <div className="bg-base-200 flex h-12 w-12 items-center justify-center rounded-lg">
                                                <MdStorefront className="text-base-content/30 text-2xl" />
                                            </div>
                                        )}
                                        <div>
                                            <h3 className="font-bold">
                                                {store.name}
                                            </h3>
                                            <p className="text-base-content/50 text-sm">
                                                {store.subdomain}.cliquei.shop
                                            </p>
                                        </div>
                                    </div>
                                    <span
                                        className={`badge badge-sm ${store.active ? 'badge-success' : 'badge-ghost'}`}
                                    >
                                        {store.active ? 'Ativa' : 'Inativa'}
                                    </span>
                                </div>

                                {store.description && (
                                    <p className="text-base-content/60 mt-2 line-clamp-2 text-sm">
                                        {store.description}
                                    </p>
                                )}

                                <div className="card-actions mt-4 flex-wrap justify-end">
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={() =>
                                            navigate(`/commerce/stores/edit/${store.id}`)
                                        }
                                    >
                                        <MdEdit className="text-lg" />
                                        Editar
                                    </button>
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={() =>
                                            navigate(
                                                `/commerce/themes/${store.id}`
                                            )
                                        }
                                    >
                                        <MdPalette className="text-lg" />
                                        Tema
                                    </button>
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={() =>
                                            navigate(`/commerce/stores/${store.id}/shipping`)
                                        }
                                    >
                                        <MdLocalShipping className="text-lg" />
                                        Frete
                                    </button>
                                    <button
                                        className="btn btn-ghost btn-sm text-error"
                                        onClick={() => {
                                            setDeletingId(store.id)
                                            setDeleteModalOpen(true)
                                        }}
                                    >
                                        <MdDelete className="text-lg" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false)
                    setDeletingId(null)
                }}
                onConfirm={handleDelete}
                title="Remover Loja"
                message="Tem certeza que deseja remover esta loja? Os produtos vinculados não serão removidos."
                loading={deleting}
                confirmButtonText="Remover"
                confirmButtonClass="btn-error"
            />

            <ConfirmModal
                isOpen={storeLimitModal}
                onClose={() => {
                    setStoreLimitModal(false)
                    setStoreLimitInfo(null)
                }}
                onConfirm={() => {
                    setStoreLimitModal(false)
                    setStoreLimitInfo(null)
                    navigate('/commerce/stores/new')
                }}
                title="Loja Extra"
                message={
                    storeLimitInfo
                        ? `Você atingiu o limite de lojas do seu plano. O valor da sua assinatura passará de R$ ${(storeLimitInfo.currentPrice / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} para R$ ${(storeLimitInfo.newPrice / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} por mês ao criar esta loja. Deseja continuar?`
                        : ''
                }
                confirmButtonText="Criar Loja"
                confirmButtonClass="btn-secondary"
            />
        </div>
    )
}
